import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaFileContract, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import '../assets/css/TermsConditions.css';

const TermsConditions = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setIsMobile(width <= 767);
    setIsTablet(width >= 768 && width <= 991);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize]);

  const handleOrientationChange = () => {
    setTimeout(handleResize, 100);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    e.currentTarget.style.transform = 'scale(0.98)';
    e.currentTarget.style.transition = 'transform 0.1s ease';
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    setTimeout(() => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.transition = 'transform 0.3s ease';
    }, 100);
  };

  return (
    <div className="terms-page">
      <div className="terms-container">
        {/* Header Section */}
        <div className="terms-header">
          <div className="terms-icon">
            <FaFileContract />
          </div>
          <h1 className="terms-title">Terms & Conditions</h1>
          <p className="terms-subtitle">
            {isMobile 
              ? 'Please read our terms carefully before using our services.'
              : 'Welcome to Design Craft Office Furniture. By accessing or using our website www.designcraft.ae, and engaging with our products or services, you agree to comply with and be bound by the following terms and conditions. Please read them carefully.'
            }
          </p>
        </div>

        {/* Content Section */}
        <div className="terms-content">
          {/* Section 1 */}
          <div className="terms-section">
            <h2>1. General Terms</h2>
            <ul>
              <li>These Terms govern your access to and use of our website and services.</li>
              <li>By using our site, you confirm that you are legally capable of entering into binding agreements.</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="terms-section">
            <h2>2. Products & Services</h2>
            <ul>
              <li>All products and services are subject to availability and may be modified or discontinued without notice.</li>
              <li>Product descriptions, images, and specifications are provided for general reference and may vary slightly due to custom orders or material changes.</li>
              <li>Custom-made items require clear specifications and confirmation from the client before production.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="terms-section">
            <h2>3. Pricing & Payments</h2>
            <ul>
              <li>All prices are quoted in AED (United Arab Emirates Dirham) unless otherwise stated.</li>
              <li>We reserve the right to change pricing at any time without prior notice.</li>
              <li>Payments must be made as per the agreed terms in the invoice or quotation. Delayed payments may incur additional charges or result in halted deliveries.</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="terms-section">
            <h2>4. Orders & Cancellations</h2>
            <ul>
              <li>Orders for custom furniture are confirmed only after receiving a formal purchase order and agreed-upon advance payment.</li>
              <li>Cancellations must be made in writing within 24 hours of order confirmation. After production begins, cancellations may not be accepted and any incurred costs will be charged.</li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="terms-section">
            <h2>5. Delivery & Installation</h2>
            <ul>
              <li>Delivery timelines are estimated and may vary based on project size, customization, or unforeseen circumstances.</li>
              <li>We will notify you of the expected delivery and coordinate for smooth installation.</li>
              <li>It is the customer's responsibility to ensure site readiness for furniture delivery and installation.</li>
            </ul>
          </div>

          {/* Section 6 */}
          <div className="terms-section">
            <h2>6. Warranty & Claims</h2>
            <ul>
              <li>We offer a limited warranty on most products against manufacturing defects, subject to terms agreed at the time of purchase.</li>
              <li>Warranty does not cover wear and tear, misuse, accidental damage, or improper handling post-installation.</li>
              <li>Any claim must be raised within 7 days of delivery.</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div className="terms-section">
            <h2>7. Intellectual Property</h2>
            <ul>
              <li>All content on this website including text, images, logos, and designs is the property of Design Craft and protected by UAE copyright laws.</li>
              <li>You may not reproduce, distribute, or use any content without our written permission.</li>
            </ul>
          </div>

          {/* Section 8 */}
          <div className="terms-section">
            <h2>8. Limitation of Liability</h2>
            <ul>
              <li>Design Craft is not liable for any direct, indirect, or incidental damages resulting from the use of our products or website.</li>
              <li>We are not responsible for any delays or damages due to events beyond our control (e.g., natural disasters, supplier issues, or government restrictions).</li>
            </ul>
          </div>

          {/* Section 9 */}
          <div className="terms-section">
            <h2>9. Privacy Policy</h2>
            <p>Please refer to our <Link to="/privacy" className="terms-link">Privacy Policy</Link> for details on how we collect, use, and protect your personal information.</p>
          </div>

          {/* Section 10 */}
          <div className="terms-section">
            <h2>10. Governing Law</h2>
            <p>These Terms and Conditions are governed by the laws of the United Arab Emirates. Any disputes will be subject to the jurisdiction of the courts in Dubai, UAE.</p>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="terms-cta">
          <h3>Questions about these Terms?</h3>
          <p>
            {isMobile 
              ? 'Contact us for any questions regarding our terms and conditions.'
              : 'For any questions or concerns regarding these Terms and Conditions, please don\'t hesitate to reach out to our team.'
            }
          </p>
          
          <div className="contact-info">
            <div className="contact-item">
              <FaMapMarkerAlt className="contact-icon" />
              <div>
                <strong>Design Craft Office Furniture</strong><br />
                Abdul Razzaq Mohiddin Abdulla<br />
                Warehouse Shed No-1 Opp. Pedal Art<br />
                Al Quoz Industrial Area – 1, Dubai
              </div>
            </div>
            
            <div className="contact-item">
              <FaEnvelope className="contact-icon" />
              <div>
                <a href="mailto:info@designcraft.ae">info@designcraft.ae</a>
              </div>
            </div>
            
            <div className="contact-item">
              <FaPhone className="contact-icon" />
              <div>
                <a href="tel:+971501989883">+971 50 198 9883</a>
              </div>
            </div>
          </div>

          <div className="cta-buttons">
            <Link 
              to="/contact" 
              className="cta-button primary"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              Contact Us
            </Link>
            <Link 
              to="/privacy" 
              className="cta-button secondary"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions; 